import React from 'react';

import { RESERVATION_SEAT_TYPE, arrayOf, calcValue, isDate, isDefined, isEmpty, sortBy, valOrThrow } from '@nfctron/nexus-core';
import {
	UiAlert,
	UiAlertDescription,
	UiAlertHeader,
	UiAlertTitle,
	UiButton,
	UiDrawer,
	UiDrawerContent,
	UiDrawerTitle,
	cn,
	useContextRequired,
	useFormat,
} from '@nfctron/nexus-react';
import { Seat, type SeatTypes, SectorMap, VenueMap } from '@nfctron/nexus-tickets';

import { ActionIcon, Alert, Collapse, Container, Divider, Progress, Skeleton, Text, Title } from '@mantine/core';
import { useDisclosure, useElementSize } from '@mantine/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { CheckIcon, ChevronDownIcon, ChevronLeftIcon, ChevronUpIcon, InfoIcon, RotateCwIcon, TriangleAlertIcon } from 'lucide-react';

import { ENTRY_QUEUE_INTERVAL, getUseEntryQueueKey } from '@root/cart/_resources/hooks/useEntryQueue';
import { TicketsViewHeaderAtom } from '@root/cart/cart/_resources/atoms/TicketsView/TicketsViewHeader.atom';
import { TicketCardConfig } from '@root/cart/cart/_resources/components/Ticket/TicketCardConfig';
import { TicketTypeCard } from '@root/cart/cart/_resources/components/Ticket/TicketTypeCard';

import { CategoryBadge } from '@root/lib/components/CategoryBadge/CategoryBadge';
import { LoadingOverlay } from '@root/lib/components/LoadingOverlay/LoadingOverlay';
import { LoadingProgress } from '@root/lib/components/LoadingProgress/LoadingProgress';
import { useLoadingProgress } from '@root/lib/components/LoadingProgress/useLoadingProgress';
import { MultiView } from '@root/lib/components/MultiView/MultiView';
import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useNowState } from '@root/lib/hooks/useNow';
import { useTranslate } from '@root/lib/hooks/useTranslation';

import { EventCartPageContext } from '../components/CartContext';
import {
	CART_VIEW,
	cartStoreSelectors,
	useCartStore,
	useIsCartProcessing,
	useSeatingApi,
	useSeatingCategoriesApi,
	useSeatingMutations,
} from '../stores/cartStore';

import { CartViewContentAtom } from './CartView/CartViewContent.atom';
import { CheckoutViewContentAtom } from './CheckoutView/CheckoutViewContent.atom';
import { NoTicketsAvailableAtom } from './NoTicketsAvailable.atom';
import { ProductsViewContentAtom } from './ProductsView/ProductsViewContent.atom';
import { StepperControlAtom } from './StepperControl.atom';
import { TicketsViewContentAtom } from './TicketsView/TicketsViewContent.atom';
import { TicketsViewWithImportContentAtom } from './TicketsView/TicketsViewWithImportContent.atom';

const SectorSheetContent: React.FC<{
	openNestedId: string | null;
	isBadgeOpen: string | null;
	setOpenNestedId: React.Dispatch<React.SetStateAction<string | null>>;
	setIsBadgeOpen: React.Dispatch<React.SetStateAction<string | null>>;
}> = ({ openNestedId, setOpenNestedId, isBadgeOpen, setIsBadgeOpen }) => {
	const { t } = useTranslate(APP_I18N_NAMESPACE.MAIN, 'Event/Cart/Page');

	const { createSeatReservationMutation, createCrowdReservationMutation } = useSeatingMutations();
	const selectedSeat = useCartStore((s) => (isDefined(s.selectedSeatId) ? s.getSeatById({ seatId: s.selectedSeatId }) : null));
	const selectedCrowd = useCartStore((s) => (isDefined(s.selectedCrowdId) ? s.getCrowdById({ crowdId: s.selectedCrowdId }) : null));
	const openedSector = useCartStore(
		(s) => (isDefined(s.openedSectorId) ? s.getSectorByIdWithSeatsAndCrowds({ sectorId: s.openedSectorId }) : null),
		'deep',
	);
	const seatingReservation = useCartStore((s) => s.seatingReservation);
	const unselectCrowd = useCartStore((s) => s.unselectCrowd);
	const unselectSeat = useCartStore((s) => s.unselectSeat);
	const getTicketTypeStateById = useCartStore((s) => s.getTicketTypeStateById);

	const ticketsFromSeat = useCartStore((s) => {
		if (!isDefined(selectedSeat)) return [];
		return s.cartedTickets.filter((ct) => ct.reservation.seat?.seatId === selectedSeat.seatId) ?? [];
	}, 'deep');
	const ticketsFromCrowd = useCartStore((s) => {
		if (!isDefined(selectedCrowd)) return [];
		return s.cartedTickets.filter((ct) => ct.reservation.crowd?.crowdId === selectedCrowd.crowdId) ?? [];
	}, 'deep');

	const selectedCrowdMeta = useCartStore((state) => {
		if (!isDefined(selectedCrowd) || !selectedCrowd.crowdId) return null;
		const crowdIs = state.getCrowdStateById({ crowdId: selectedCrowd.crowdId });
		const category = state.getCategoryById({ categoryId: selectedCrowd.categoryId });
		const ticketTypes = cartStoreSelectors.listTicketTypesForCategory(state, { categoryId: selectedCrowd.categoryId });
		return { crowdIs, category, ticketTypes };
	}, 'deep');

	const selectedSeatMeta = useCartStore((state) => {
		if (!isDefined(selectedSeat) || !selectedSeat.seatId) return null;
		const seatIs = state.getSeatStateById({ seatId: selectedSeat.seatId });
		const category = state.getCategoryById({ categoryId: selectedSeat.categoryId });
		const ticketTypes = cartStoreSelectors.listTicketTypesForCategory(state, { categoryId: selectedSeat.categoryId });
		return { seatIs, category, ticketTypes };
	}, 'deep');

	const errorAlert = isDefined(createSeatReservationMutation.error) ? (
		<Alert color="red" icon={<TriangleAlertIcon className="text-xl" />} className="mb-3" radius="md">
			<Text c="red" size="md">
				{t('sheet.seatReservationError.text')}
			</Text>
		</Alert>
	) : (
		isDefined(createCrowdReservationMutation.error) && (
			<Alert color="red" icon={<TriangleAlertIcon className="text-xl" />} className="mb-3" radius="md">
				<Text c="red" size="md">
					{t('sheet.crowdReservationError.text')}
				</Text>
			</Alert>
		)
	);

	/** selected seat */
	if (isDefined(selectedSeat) && isDefined(selectedSeatMeta)) {
		return (
			<React.Fragment>
				{/* error alert */}
				{errorAlert}

				{/* content */}
				<div className="flex flex-col gap-1">
					{/* header badge */}
					<svg width="100%" height={5}>
						<rect fill={selectedSeatMeta.category.color} width="100%" height="100%" rx={2.5} ry={2.5} />
					</svg>
					{/* main content */}
					<div className="flex flex-col gap-2 p-2">
						{/* header */}
						<div className="flex items-center justify-between gap-2">
							{/* seat name */}
							<Text fz={20} c="lucky-point">
								{selectedSeat.fullName}
							</Text>
							{/* sector category */}
							<CategoryBadge category={selectedSeatMeta.category} />
						</div>

						{/* seating info */}
						<div className="grid grid-cols-4 gap-3">
							{/* sector */}
							<div className="text-left">
								<Text fz={14} c="gray" className="uppercase">
									{t('sheet.sectorLabel.text')}
								</Text>
								<Text fz={20} c="lucky-point" fw={700} className="truncate">
									{openedSector?.label}
								</Text>
							</div>
							{/* row */}
							<div className="col-span-2 text-center">
								<Text fz={14} c="gray" className="uppercase">
									{t('sheet.seatRowLabel.text')}
								</Text>
								<Text fz={20} c="lucky-point" fw={700} className="truncate">
									{selectedSeat.seatRow}
								</Text>
							</div>
							{/* place */}
							<div className="text-right">
								<Text fz={14} c="gray" className="uppercase">
									{t('sheet.seatPlaceLabel.text')}
								</Text>
								<Text fz={20} c="lucky-point" fw={700} className="truncate">
									{selectedSeat.label}
								</Text>
							</div>
						</div>
					</div>
					{/* CTA button */}
					{selectedSeatMeta.seatIs.unavailable ? (
						<React.Fragment>
							{selectedSeatMeta.seatIs.soldOut ? (
								/** sold out seat button */
								<UiButton fullWidth variant="primary" disabled>
									<span>{t('sheet.seatSoldOutButton.text')}</span>
								</UiButton>
							) : (
								!selectedSeatMeta.seatIs.saleable && (
									/** not saleable seat button */
									<UiButton fullWidth variant="primary" disabled>
										<span>{t('sheet.seatNotSaleableButton.text')}</span>
									</UiButton>
								)
							)}
						</React.Fragment>
					) : (
						<React.Fragment>
							{/* ticket/ticketTypes */}
							<div className="">
								{ticketsFromSeat.length > 0 ? (
									ticketsFromSeat.map((ct) => (
										<TicketCardConfig
											readOnly
											key={ct.cartTicketId}
											dividerBgColor="#FFF"
											className="border border-gray-200 bg-gray-50!"
											cartTicketId={ct.cartTicketId}
										/>
									))
								) : (
									<div className="flex flex-col gap-3">
										{selectedSeatMeta.ticketTypes.map((ticketType) => {
											const isReserved = seatingReservation?.seats.some((rs) => rs.seatId === selectedSeat?.seatId);
											const ticketTypeIs = getTicketTypeStateById({ ticketTypeId: ticketType.ticketTypeId });

											const controller = isReserved ? (
												<UiButton variant="filled" color="green" leftSection={<CheckIcon className="text-sm" />} disabled>
													{t('sheet.seatReservedButton.text')}
												</UiButton>
											) : (
												<UiButton
													loading={createSeatReservationMutation.isPending}
													disabled={ticketTypeIs.soldOut || ticketTypeIs.orderLimited}
													onClick={async () => {
														/** reserve the seat and add the ticket to the cart */
														await createSeatReservationMutation.mutateAsync({
															seatId: valOrThrow(selectedSeat, 'no selected seat found').seatId,
															ticketTypeId: ticketType.ticketTypeId,
														});
													}}
												>
													{t('sheet.seatReserveButton.text')}
												</UiButton>
											);

											return (
												<TicketTypeCard
													className="border border-gray-200 bg-gray-50!"
													key={ticketType.ticketTypeId}
													ticketType={ticketType}
													controller={controller}
													openNestedId={openNestedId}
													isBadgeOpen={isBadgeOpen}
													setOpenNestedId={setOpenNestedId}
													setIsBadgeOpen={setIsBadgeOpen}
												/>
											);
										})}
									</div>
								)}
							</div>

							{/* close button */}
							<UiButton className="mt-3" fullWidth variant="primary" onClick={unselectSeat}>
								<span>{t('sheet.closeButton.text')}</span>
							</UiButton>
						</React.Fragment>
					)}
				</div>
			</React.Fragment>
		);
	}

	/** selected crowd */
	if (isDefined(selectedCrowd) && isDefined(selectedCrowdMeta)) {
		return (
			<React.Fragment>
				{/* error alert */}
				{errorAlert}

				{/* content */}
				<div className="flex flex-col gap-1 overflow-x-hidden overflow-y-auto">
					{/* header badge */}
					<svg width="100%" height={5}>
						<rect fill={selectedCrowdMeta.category.color} width="100%" height="100%" rx={2.5} ry={2.5} />
					</svg>

					{/* main content */}
					<div className="flex flex-col gap-2 p-2">
						{/* header */}
						<div className="flex items-center justify-between gap-2">
							{/* crowd name */}
							<Text fz={20} c="lucky-point">
								{selectedCrowd.name}
							</Text>
							{/* sector category */}
							<CategoryBadge category={selectedCrowdMeta.category} />
						</div>

						{/* crowd description */}
						{!isEmpty(selectedCrowd.description) && (
							<Text size="md" c="dimmed">
								{selectedCrowd.description}
							</Text>
						)}

						{/* seating crowd info */}
						<div className="grid grid-cols-3 gap-3">
							{/* sector */}
							<div className="text-left">
								<Text fz={14} c="gray" className="uppercase">
									{t('sheet.sectorLabel.text')}
								</Text>
								<Text fz={18} c="lucky-point" fw={700} className="truncate">
									{openedSector?.label}
								</Text>
							</div>
							{/* crowd name */}
							<div className="text-center">
								<Text fz={14} c="gray" className="uppercase">
									{t('sheet.crowdNameLabel.text')}
								</Text>
								<Text fz={18} c="lucky-point" fw={700} className="truncate">
									{selectedCrowd.name}
								</Text>
							</div>
							{/* capacity */}
							<div className="text-right">
								<Text fz={14} c="gray" className="uppercase">
									{t('sheet.crowdCapacityLeftLabel.text')}
								</Text>
								<Text fz={18} c="lucky-point" fw={700} className="truncate">
									<span>
										{selectedCrowd.capacityLeft > 0 ? t('sheet.crowdCapacityDescription.available') : t('sheet.crowdCapacityDescription.unavailable')}
									</span>
								</Text>
							</div>
						</div>
					</div>

					{/* CTA button */}
					{selectedCrowdMeta.crowdIs.unavailable ? (
						<React.Fragment>
							{selectedCrowdMeta.crowdIs.soldOut ? (
								/** sold out crowd button */
								<UiButton fullWidth variant="primary" disabled>
									<span>{t('sheet.crowdSoldOutButton.text')}</span>
								</UiButton>
							) : (
								!selectedCrowdMeta.crowdIs.saleable && (
									/** not saleable crowd button */
									<UiButton fullWidth variant="primary" disabled>
										<span>{t('sheet.crowdNotSaleableButton.text')}</span>
									</UiButton>
								)
							)}
						</React.Fragment>
					) : (
						<React.Fragment>
							{/* ticket/ticketTypes */}
							<div className="flex flex-col gap-3">
								{/* carted tickets */}
								{ticketsFromCrowd.length > 0 && (
									<React.Fragment>
										{/* divider */}
										<Divider className="border-gray-200!" label={t('sheet.crowdTicketsDivider.text')} labelPosition="center" />
										{/* carted tickets */}
										<div className="flex flex-col gap-2">
											{ticketsFromCrowd.map((ct) => (
												<TicketCardConfig
													readOnly
													key={ct.cartTicketId + ct.holderName}
													className="border border-gray-200 bg-gray-50!"
													cartTicketId={ct.cartTicketId}
												/>
											))}
										</div>
									</React.Fragment>
								)}
								{/* divider */}
								<Divider className="border-gray-200! text-sm" label={t('sheet.crowdTicketTypesDivider.text')} labelPosition="center" />
								{/* ticket types */}
								<div className="flex flex-col gap-3">
									{selectedCrowdMeta.ticketTypes.map((ticketType) => {
										const isReserved = seatingReservation?.crowds.some((rc) => rc.crowdId === selectedCrowd?.crowdId);
										const ticketTypeIs = getTicketTypeStateById({ ticketTypeId: ticketType.ticketTypeId });
										const controller = (
											<UiButton
												variant="primary"
												loading={createCrowdReservationMutation.isPending}
												disabled={ticketTypeIs.soldOut || ticketTypeIs.orderLimited}
												onClick={async () => {
													/** reserve the crowd and add the ticket to the cart */
													await createCrowdReservationMutation.mutateAsync({
														crowdId: valOrThrow(selectedCrowd, 'no selected crowd found').crowdId,
														ticketTypeId: ticketType.ticketTypeId,
													});
												}}
											>
												{isReserved ? t('sheet.crowdReservedButton.text') : t('sheet.crowdReserveButton.text')}
											</UiButton>
										);

										return (
											<TicketTypeCard
												className="border border-gray-200 bg-gray-50!"
												key={ticketType.ticketTypeId}
												ticketType={ticketType}
												controller={controller}
												openNestedId={openNestedId}
												isBadgeOpen={isBadgeOpen}
												setOpenNestedId={setOpenNestedId}
												setIsBadgeOpen={setIsBadgeOpen}
											/>
										);
									})}
								</div>
							</div>

							{/* close button */}
							<UiButton className="mt-3" fullWidth variant="primary" onClick={unselectCrowd}>
								<span>{t('sheet.closeButton.text')}</span>
							</UiButton>
						</React.Fragment>
					)}
				</div>
			</React.Fragment>
		);
	}

	/** nothing selected */
	return (
		<UiButton fullWidth variant="primary" disabled>
			<span>{t('sheet.defaultButton.text')}</span>
		</UiButton>
	);
};

/**
 * Event/Cart/Page atom component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const EventCartPageContent: React.FunctionComponent<{
	children?: React.ReactNode;
}> = ({ children }) => {
	const { t } = useTranslate(APP_I18N_NAMESPACE.MAIN, 'Event/Cart/Page');
	const { event } = useContextRequired(EventCartPageContext);
	const format = useFormat();
	const [isBadgeOpen, setIsBadgeOpen] = React.useState<string | null>(null);
	const [openNestedId, setOpenNestedId] = React.useState<string | null>(null);

	const { sectorApi, venueApi } = useSeatingApi();
	const { categoriesApi } = useSeatingCategoriesApi();
	const { cancelReservationMutation, createReservationMutation } = useSeatingMutations();
	const selectedSeat = useCartStore((s) => (isDefined(s.selectedSeatId) ? s.getSeatById({ seatId: s.selectedSeatId }) : null));
	const selectedCrowd = useCartStore((s) => (isDefined(s.selectedCrowdId) ? s.getCrowdById({ crowdId: s.selectedCrowdId }) : null));
	const openedSector = useCartStore(
		(s) => (isDefined(s.openedSectorId) ? s.getSectorByIdWithSeatsAndCrowds({ sectorId: s.openedSectorId }) : null),
		'deep',
	);
	const selectCrowd = useCartStore((s) => s.selectCrowd);
	const unselectCrowd = useCartStore((s) => s.unselectCrowd);
	const selectSeat = useCartStore((s) => s.selectSeat);
	const getCrowdStateById = useCartStore((s) => s.getCrowdStateById);
	const unselectSeat = useCartStore((s) => s.unselectSeat);
	const getSeatStateById = useCartStore((s) => s.getSeatStateById);

	const categoriesListed = useCartStore((s) => cartStoreSelectors.listCategories(s));
	const categoriesAll = useCartStore((s) => s.categoriesConfig);

	const cartState = useCartStore((s) => cartStoreSelectors.getCartState(s));

	/** get width and height of the container */
	const { ref: containerRef, width, height } = useElementSize();

	/** having ticket categories means seating is enabled */
	const isSeatingEnabled = useCartStore((s) => s.ticketTypesConfig.some((tt) => tt.isReservable));

	/** memoized sector view node */
	const sectorViewNode = React.useMemo(() => {
		/** error loading */
		if (isDefined(sectorApi.error)) {
			return (
				<div className="flex h-full flex-col items-center justify-center gap-6 bg-red-50">
					<div>
						<TriangleAlertIcon size={55} className="mx-auto mb-2 text-red-500" />
						{/* info text */}
						<Text c="red" size="md" className="text-center">
							{t('sectorView.error.text')}
						</Text>
					</div>
					{/* reload button */}
					<UiButton
						color="red"
						variant="filled"
						onClick={async () => sectorApi.refetch()}
						leftSection={<RotateCwIcon size={16} />}
						loading={sectorApi.isLoading}
					>
						{t('sectorView.error.button')}
					</UiButton>
				</div>
			);
		}

		/** sector map */
		if (isDefined(openedSector)) {
			return (
				<SectorMap
					width={width}
					height={height}
					sector={openedSector}
					categories={categoriesAll}
					onReload={() => sectorApi.refetch()}
					isReloading={sectorApi.isRefetching}
					/** crowd handlers */
					onCrowdSelect={(crowdId) => selectCrowd({ crowdId })}
					onCrowdUnselect={unselectCrowd}
					getCrowdProps={({ crowdId }) => {
						return {
							crowdIs: getCrowdStateById({ crowdId }),
							svgProps: {},
						};
					}}
					/** seat handlers */
					onSeatSelect={(seatId) => selectSeat({ seatId })}
					onSeatUnselect={unselectSeat}
					getSeatProps={({ seatId }) => {
						const seatIs = getSeatStateById({ seatId });
						return {
							seatIs,
							svgProps: {},
						};
					}}
				/>
			);
		}

		/** loading */
		return <Skeleton height={height} radius={0} />;
	}, [
		sectorApi,
		openedSector,
		height,
		t,
		width,
		categoriesAll,
		unselectCrowd,
		unselectSeat,
		selectCrowd,
		getCrowdStateById,
		selectSeat,
		getSeatStateById,
	]);

	const openSector = useCartStore((s) => s.openSector);
	const closeSector = useCartStore((s) => s.closeSector);
	const getSectorStateById = useCartStore((s) => s.getSectorStateById);

	const venueConfig = useCartStore((s) => s.venueConfig);
	const venueViewNode = React.useMemo(() => {
		/** error loading */
		if (isDefined(venueApi.error)) {
			return (
				<div className="flex h-full flex-col items-center justify-center gap-6 bg-red-50">
					<div>
						<TriangleAlertIcon size={55} className="mx-auto mb-2 text-red-500" />
						{/* info text */}
						<Text c="red" size="md" className="text-center">
							{t('venueView.error.text')}
						</Text>
					</div>
					{/* reload button */}
					<UiButton
						color="red"
						variant="filled"
						onClick={async () => venueApi.refetch()}
						leftSection={<RotateCwIcon size={16} />}
						loading={venueApi.isLoading}
					>
						{t('venueView.error.button')}
					</UiButton>
				</div>
			);
		}

		/** venue map */
		if (isDefined(venueConfig)) {
			return (
				<VenueMap
					width={width}
					height={height}
					venue={venueConfig}
					categories={categoriesAll}
					/** sector handlers */
					onSectorSelect={(sectorId) => openSector({ sectorId })}
					onSectorUnselect={closeSector}
					getIsSector={(sectorId) => getSectorStateById({ sectorId })}
				/>
			);
		}

		/** loading */
		return <Skeleton height={height} radius={0} />;
	}, [venueApi, venueConfig, height, t, width, categoriesAll, closeSector, openSector, getSectorStateById]);

	/** loading progress state management */
	const { provider: loadingProgressProvider } = useLoadingProgress({
		deps: [venueApi.isLoading, sectorApi.isLoading],
	});

	/** bottom legend */
	const [legendOpen, legendDisclosure] = useDisclosure(false);
	const getMockSeatSvg = (seatIs: Partial<ReturnType<SeatTypes.Props['getSeatProps']>['seatIs']>) => {
		const category = categoriesAll[0];
		if (!isDefined(category)) return null;
		return (
			<svg width={21} height={21} viewBox="-250 -250 500 500">
				<Seat
					seat={{
						seatId: '',
						category,
						fullName: '',
						seatRow: '',
						place: 42,
						seatTypeId: RESERVATION_SEAT_TYPE.CIRCLE_50CM,
						capacityLeft: 0,
						label: '42',
						position: {
							x: 0,
							y: 0,
							rotation: 0,
						},
					}}
					getSeatProps={() => ({
						seatIs: {
							saleable: false,
							soldOut: false,
							unavailable: false,
							carted: false,
							selected: false,
							...seatIs,
						},
					})}
					onSeatSelect={() => {}}
					onSeatUnselect={() => {}}
				/>
			</svg>
		);
	};

	const queryClient = useQueryClient();
	const useEntryQueueQueryData = queryClient.getQueryData<any>(getUseEntryQueueKey(event.eventId, event.organizer.slug));
	const isEntryQueueActive = isDefined(useEntryQueueQueryData) && useEntryQueueQueryData.shouldQueue;

	/** get progress bar value (0-100) as time remaining to the next check */
	const now = useNowState({ interval: 1, autoStart: isDefined(useEntryQueueQueryData) });
	const progressValue = calcValue(() => {
		if (!isDefined(useEntryQueueQueryData)) return 100;
		if (!isDate(useEntryQueueQueryData.nextCheck)) return 100;
		const nextCheck = useEntryQueueQueryData.nextCheck;

		const diff = nextCheck.getTime() - now.getTime();
		const progress = (diff / ENTRY_QUEUE_INTERVAL) * 100;
		return Math.max(0, Math.min(100, progress));
	});

	const cartProcessing = useIsCartProcessing();

	return (
		<div className="relative z-10 flex grow flex-col">
			{/* reservation processing loading overlay */}
			<LoadingOverlay
				visible={cancelReservationMutation.isPending || createReservationMutation.isPending}
				message={t('loadingOverlay.reservationProcessing.text')}
			/>

			{/* order processing loading overlay */}
			<LoadingOverlay visible={cartProcessing.isOrderProcessing} message={t('loadingOverlay.orderProcessing.text')} />

			{/* general cart processing loading overlay */}
			<LoadingOverlay visible={cartProcessing.isProcessing} message={t('loadingOverlay.cartProcessing.text')} />

			{/* main content */}
			<main
				className={cn(
					'flex min-h-[65vh] grow flex-col rounded-none bg-white px-2 py-4 sm:rounded-2xl sm:border sm:border-zinc-200 md:py-2',
					isSeatingEnabled && 'min-h-unset',
				)}
			>
				<Container className="flex w-full grow flex-col gap-2" p={0}>
					{!cartState.isCartEnabled ? (
						<NoTicketsAvailableAtom />
					) : isEntryQueueActive ? (
						<div className="relative flex w-full grow flex-col items-center justify-center overflow-hidden rounded-lg border border-gray-200">
							<div className="z-10 m-0 rounded-lg border-gray-200 bg-white p-4 md:mx-8 md:my-4 md:grow-0 md:border">
								<div className="m-auto flex w-full flex-col gap-3 text-center md:p-4">
									{/* logo */}
									{!isEmpty(event.logoUrl) && (
										<div className="my-6 flex w-full items-center justify-center px-4">
											<img src={event.logoUrl} alt={event.namePub} height="100px" className="max-w-[200px]" />
										</div>
									)}

									{/* queue title */}
									<Title size="h3" c="lucky-point" fw="bold">
										Jste v pořadí!
									</Title>
									{/* queue text */}
									<Text color="dimmed">
										Z důvodu velkého zájmu o vstupenky jste byli zařazeni do fronty. Jakmile budete na řadě, budete automaticky přesměrováni na
										stránku s výběrem vstupenek.
									</Text>
									{isDefined(useEntryQueueQueryData) && isDefined(useEntryQueueQueryData.currentPlace) && (
										<div className="p-2">
											<Text size="xl" fw={700} c="lucky-point">
												{format.number(useEntryQueueQueryData.currentPlace)} lidí před vámi
											</Text>
										</div>
									)}
									{/* queue alert */}
									<UiAlert className="text-left" color="indigo" variant="subtle">
										<UiAlertHeader>
											<InfoIcon />
											<UiAlertTitle>Tuto stránku neopouštějte</UiAlertTitle>
										</UiAlertHeader>
										<UiAlertDescription>Pokud stránku opustíte, bude vaše místo v řadě uvolněno pro další zájemce.</UiAlertDescription>
									</UiAlert>
									{/* queue progress */}
									<Progress animated color="indigo" value={progressValue} />
									{/* queue check button*/}
									<UiButton variant="primary" size="lg" disabled={useEntryQueueQueryData?.shouldQueue}>
										Vyčkejte prosím...
									</UiButton>
								</div>

								{isDefined(useEntryQueueQueryData) && isDefined(useEntryQueueQueryData.lastCheck) && (
									<React.Fragment>
										<Divider my="sm" />

										<div className="flex items-center justify-between gap-2">
											<Text size="md" color="dimmed">
												Poslední kontrola fronty
											</Text>
											<Text size="md" color="dimmed">
												{format.date(useEntryQueueQueryData.lastCheck, 'PPpp')}
											</Text>
										</div>
									</React.Fragment>
								)}
							</div>

							<div className="absolute hidden w-full flex-col items-center justify-start gap-3 justify-self-start p-2 opacity-50 blur-xs md:flex">
								{/* dummy ticket type like cards */}
								{arrayOf(10).map((key) => (
									<div key={key} className="h-[125px] w-full rounded-lg border border-gray-400 bg-gray-200" />
								))}
							</div>
						</div>
					) : (
						<React.Fragment>
							{/* stepper control card */}
							<StepperControlAtom />

							{/* children */}
							{children}

							<React.Fragment>
								{/* tickets view */}
								<MultiView.View view={CART_VIEW.TICKETS}>
									{/* import providers (if any) */}
									<TicketsViewWithImportContentAtom />

									{/* normal tickets view */}
									{!isSeatingEnabled && cartState.isTicketSaleEnabledOrHasTickeTypes && (
										<React.Fragment>
											{/* header + promo codes */}
											<TicketsViewHeaderAtom />
											{/* tickets view content */}
											<TicketsViewContentAtom />
										</React.Fragment>
									)}
									{/* seating tickets view */}
									{isSeatingEnabled && (
										<React.Fragment>
											{/* header + promo codes */}
											<TicketsViewHeaderAtom />

											{/* seating tickets view content */}
											<div
												className="relative flex h-[calc(90dvh-100px)] max-h-screen w-full flex-col overflow-hidden rounded-lg border border-gray-300 bg-gray-50 xl:h-[735px]"
												ref={containerRef}
											>
												{/* top nav bar */}
												{isDefined(venueApi.data) && (
													<div className="m-2 flex flex-col gap-1 rounded-lg border border-gray-200 bg-white p-3">
														{/* venue top bar */}
														{!isDefined(openedSector) && (
															<React.Fragment>
																<Text fw={700} c="lucky-point" className="text-xl xl:text-2xl">
																	{venueApi.data.venue.name}
																</Text>
																<Text className="text-base xl:text-lg" c="gray">
																	{t('venueTopBar.text')}
																</Text>
															</React.Fragment>
														)}

														{/* sector top bar */}
														{isDefined(openedSector) && (
															<React.Fragment>
																<div className="flex items-center gap-1">
																	{/* back button */}
																	{venueApi.data.sectors.length > 1 && (
																		<ActionIcon variant="transparent" radius="xl" onClick={() => closeSector()} color="lucky-point" size="md">
																			<ChevronLeftIcon className="text-xl" />
																		</ActionIcon>
																	)}
																	{/* venue name */}
																	<Text fw={700} c="lucky-point" fz={20}>
																		{venueApi.data.venue.name}
																	</Text>
																	{/* sector name */}
																	<Text c="lucky-point" />
																	<Text fw={500} c="lucky-point.2" fz={20}>
																		{openedSector.name}
																	</Text>
																</div>
																<Text size="md" c="gray">
																	{t('sectorTopBar.text')}
																</Text>
															</React.Fragment>
														)}
													</div>
												)}

												<div className="relative grow">
													{/* loading overlay */}
													<LoadingOverlay visible={cartProcessing.isSeatingLoading} message={t('loadingOverlay.seatingProcessing.text')} />
													{/* loading progress bar */}
													<LoadingProgress provider={loadingProgressProvider} />

													{/* sector view */}
													{isDefined(openedSector) && sectorViewNode}
													{/* venue view */}
													{!isDefined(openedSector) && venueViewNode}
												</div>

												{/* categories legend */}
												<div
													className={cn(
														'w-content absolute bottom-2 left-2 z-50 rounded-md border border-gray-200 bg-white p-2 shadow-xs transition-all',
													)}
												>
													{/* toggle button header */}
													<button type="button" className="flex w-full items-center gap-2" onClick={legendDisclosure.toggle}>
														<Text size="xs" fw={700} c="lucky-point">
															{t('categoryLegend.header.text')}
														</Text>

														{/* categories preview */}
														{!legendOpen && (
															<div className="flex gap-1 overflow-hidden">
																{categoriesListed.slice(0, 3).map((category) => (
																	<CategoryBadge className="text-2xs!" size="xs" key={category.categoryId} category={category} />
																))}
															</div>
														)}

														{/* toggle icon */}
														<div className="ml-auto">{legendOpen ? <ChevronDownIcon size={16} /> : <ChevronUpIcon size={16} />}</div>
													</button>

													{/* legend collapsible content */}
													<Collapse in={legendOpen}>
														<div className="mt-2 flex flex-col gap-3">
															{/* categories */}
															<div>
																<Text size="xs" fw={600} c="dimmed">
																	{t('categoryLegend.categories.text')}
																</Text>
																<div className="mt-1 flex flex-wrap gap-2">
																	{categoriesAll.sort(sortBy('ticketTypesAvailable')).map((category) => (
																		<CategoryBadge
																			className={cn('text-2xs!', category.ticketTypesAvailable === 0 && 'opacity-20')}
																			size="xs"
																			key={category.categoryId}
																			category={category}
																		/>
																	))}
																</div>
															</div>
															{/* indicators */}
															<div>
																<Text size="xs" fw={600} c="dimmed">
																	{t('categoryLegend.indicators.text')}
																</Text>
																<div className="mt-1 flex flex-wrap gap-2">
																	{/* saleable */}
																	<div className="flex items-center gap-2">
																		{getMockSeatSvg({ saleable: true })}
																		<Text className="text-sm">{t('categoryLegend.indicators.saleable.text')}</Text>
																	</div>
																	{/* soldOut */}
																	<div className="flex items-center gap-2">
																		{getMockSeatSvg({ soldOut: true })}
																		<Text className="text-sm">{t('categoryLegend.indicators.soldOut.text')}</Text>
																	</div>
																</div>
															</div>
														</div>
													</Collapse>
												</div>
											</div>
											{/* bottom sheet */}
											<UiDrawer
												open={isDefined(selectedSeat) || isDefined(selectedCrowd)}
												onClose={() => {
													if (isDefined(selectedSeat)) unselectSeat();
													if (isDefined(selectedCrowd)) unselectCrowd();
												}}
											>
												<UiDrawerContent className={`mx-auto ${openNestedId || isBadgeOpen ? 'hidden' : ''} w-full max-w-screen-sm p-3`}>
													{/* UiDrawer content requires a title to be present */}
													<UiDrawerTitle className="hidden" />
													{/* sector footer view */}
													<SectorSheetContent
														openNestedId={openNestedId}
														isBadgeOpen={isBadgeOpen}
														setOpenNestedId={setOpenNestedId}
														setIsBadgeOpen={setIsBadgeOpen}
													/>
												</UiDrawerContent>
											</UiDrawer>
										</React.Fragment>
									)}
								</MultiView.View>
							</React.Fragment>

							{/* cart view */}
							<MultiView.View view={CART_VIEW.CART}>
								<CartViewContentAtom />
							</MultiView.View>

							{/* products view */}
							<MultiView.View view={CART_VIEW.PRODUCTS}>
								<ProductsViewContentAtom />
							</MultiView.View>

							{/* checkout view */}
							<MultiView.View view={CART_VIEW.CHECKOUT}>
								<CheckoutViewContentAtom />
							</MultiView.View>
						</React.Fragment>
					)}
				</Container>
			</main>
		</div>
	);
};

/**
 * Event/Cart/EventCartPageContent atom display name
 * @type {ComponentDisplayName<"atom">>}
 */
EventCartPageContent.displayName = 'Event/Cart/Page/Content';
